import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import React, { useState } from 'react';
import {
    Bar, BarChart, CartesianGrid,
    Legend,
    PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart,
    ResponsiveContainer,
    Tooltip,
    XAxis, YAxis,
} from 'recharts';
import { FadedPaper } from '../../theme/StyledFields';
import KeyTakeaway from './KeyTakeaway';
import VizToggle, { DEFAULT_REPORT_VIZ, VizType } from './VizToggle';
import { TierSection as TierSectionData } from './types';

const UPPER_COLOR = '#8884d8';
const LOWER_COLOR = '#ff7f50';

interface Props {
    data: TierSectionData;
}

const TierSection: React.FC<Props> = ({ data }) => {
    const [viz, setViz] = useState<VizType>(DEFAULT_REPORT_VIZ);

    const allDomains = Array.from(
        new Set([
            ...data.upper_half.map((r) => r.domain_name),
            ...data.lower_half.map((r) => r.domain_name),
        ])
    );

    const radarData = allDomains.map((domain) => {
        const upper = data.upper_half.find((r) => r.domain_name === domain);
        const lower = data.lower_half.find((r) => r.domain_name === domain);
        return {
            domain,
            upper: upper?.avg_score ?? 0,
            lower: lower?.avg_score ?? 0,
        };
    });

    const barData = allDomains.map((domain) => {
        const upper = data.upper_half.find((r) => r.domain_name === domain);
        const lower = data.lower_half.find((r) => r.domain_name === domain);
        return {
            domain,
            upper: upper?.avg_score ?? 0,
            lower: lower?.avg_score ?? 0,
        };
    });

    return (
        <FadedPaper sx={{ p: 3, mb: 3 }} data-testid="section-tier-segmentation">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h5">Where the Biggest Skill Gaps Appear</Typography>
                <VizToggle value={viz} options={['radar', 'bar', 'table']} onChange={setViz} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upper vs. lower half by total score. Reveals whether the lower half shares
                specific growth opportunities, or is uniformly lower.
            </Typography>

            {viz === 'radar' && (
                <Box sx={{ width: '100%', maxWidth: 500, mx: 'auto' }}>
                    <ResponsiveContainer width="100%" height={400}>
                        <RadarChart data={radarData}>
                            <PolarGrid />
                            <PolarAngleAxis dataKey="domain" tick={{ fontSize: 11 }} />
                            <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 10 }} />
                            <Radar name="Upper Half" dataKey="upper" stroke={UPPER_COLOR} fill={UPPER_COLOR} fillOpacity={0.3} strokeWidth={2} />
                            <Radar name="Lower Half" dataKey="lower" stroke={LOWER_COLOR} fill={LOWER_COLOR} fillOpacity={0.15} strokeWidth={2} />
                            <Legend />
                        </RadarChart>
                    </ResponsiveContainer>
                </Box>
            )}

            {viz === 'bar' && (
                <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={barData} layout="vertical" margin={{ left: 120 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 5]} />
                        <YAxis type="category" dataKey="domain" tick={{ fontSize: 12 }} width={110} />
                        <Tooltip />
                        <Bar dataKey="upper" name="Upper Half" fill={UPPER_COLOR} radius={[0, 4, 4, 0]} />
                        <Bar dataKey="lower" name="Lower Half" fill={LOWER_COLOR} radius={[0, 4, 4, 0]} />
                        <Legend />
                    </BarChart>
                </ResponsiveContainer>
            )}

            {viz === 'table' && (
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {[
                        { label: 'Upper Half', rows: data.upper_half },
                        { label: 'Lower Half', rows: data.lower_half },
                    ].map(({ label, rows }) => (
                        <Box key={label} sx={{ flex: 1, minWidth: 300 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                {label} (n={rows[0]?.n ?? 0})
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Domain</TableCell>
                                            <TableCell align="right">Avg</TableCell>
                                            <TableCell align="right">Min</TableCell>
                                            <TableCell align="right">Max</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((r) => (
                                            <TableRow key={r.domain_name}>
                                                <TableCell>{r.domain_name}</TableCell>
                                                <TableCell align="right">{r.avg_score}</TableCell>
                                                <TableCell align="right">{r.min_score}</TableCell>
                                                <TableCell align="right">{r.max_score}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ))}
                </Box>
            )}

            <KeyTakeaway text={data.key_takeaway} />
        </FadedPaper>
    );
};

export default TierSection;
